// React imports
import React from 'react';
import classNames from 'classnames';
import { Chevron } from '@codametrix/ui-components';

/**
 * Props for the Menu Button component
 */
type MenuButtonProps = {
  label?: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  prefixTitle?: any;
  icon?: JSX.Element;
  style?: any;
  defaultLabel?: string;
  secondaryLabel?: string;
};

/**
 * State for the Menu Button component
 */
type MenuButtonState = {
  /** Whether or not the menu is currently visible */
  showMenu: boolean;
};

/**
 * A menu button that displays a dropdown menu when clicked
 */
class MenuButton extends React.Component<MenuButtonProps, MenuButtonState> {
  menuButtonRef: React.RefObject<HTMLButtonElement>;
  menuRef: React.RefObject<HTMLDivElement>;

  /**
   * Constructor function for the component
   * @param props - The props passed to the component
   */
  constructor(props: MenuButtonProps) {
    super(props);
    this.state = { showMenu: false };
    this.menuButtonRef = React.createRef();
    this.menuRef = React.createRef();
  }

  /**
   * Toggles the visibility of the menu on click
   */
  handleMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (this.props.disabled !== true) {
      this.setState({ showMenu: !this.state.showMenu });
    }
  };

  /**
   * Handles clicks outside the menu, hiding the menu if necessary
   * @param event - the mouse event that occurred
   */
  handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      !target.closest('.menu-button-section') ||
      Array.from(target.classList).includes('chakra-select-button')
    ) {
      this.setState({ showMenu: false });
    }
  };

  handleFocus = () => {
    // Open menu when MenuButton is focused
    this.setState({ showMenu: true });
  };

  handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      const menuItems = this.menuRef.current?.querySelectorAll(
        "button, a, [tabindex='0']"
      );
      if (menuItems) {
        const lastItem = menuItems[menuItems.length - 1];

        if (!event.shiftKey && document.activeElement === lastItem) {
          this.setState({ showMenu: false }); // Close menu when tabbing out from last item
        }
      }
    }
  };

  handleBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
    setTimeout(() => {
      const focusedElement = document.activeElement as HTMLElement;

      // Close the menu only if the new focused element is NOT inside the menu or the menu button
      if (
        !this.menuRef.current?.contains(focusedElement) &&
        focusedElement !== this.menuButtonRef.current
      ) {
        this.setState({ showMenu: false });
      }
    }, 0); // Timeout allows new focus to be determined before closing the menu
  };

  /**
   * Adds an event listener for clicks outside the component, and removes it when the component unmounts
   */
  componentDidMount() {
    window.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClickOutside);
  }

  /**
   * Handles clicks on the menu, hiding the menu if item selected
   * @param event - the mouse event that occurred
   */
    handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
   
    const target = event.target as HTMLElement;

    // Check if click is on a menu item
    if (target.closest('.chakra-select-button')) {
      // Close the menu
      this.setState({ showMenu: false });
    }
  };
  /**
   * Renders the component
   * @returns The rendered component
   */
  render() {
    const {
      style,
      className,
      prefixTitle,
      label,
      icon,
      disabled,
      defaultLabel,
      secondaryLabel
    } = this.props;
    const { showMenu } = this.state;
    // Check if label prop is present, if not, use default label
    let renderedLabel = label ? label : defaultLabel;

    // Determine the class name for the label span
    const labelClassName = classNames({
      'chakra-button-label': label, // Apply chakra-button-label style if label is present
      'default-button-label': !label && defaultLabel // Apply default-button-label style if label is not present and defaultLabel is present
    });

    return (
      <div className="menu-button-section">
        <button
          style={style}
          ref={this.menuButtonRef}
          onMouseDown={this.handleMenuButtonClick}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          className={classNames(
            'chakra-secondary-button',
            className ?? '',
            'menu-main-button',
            {
              'menu-main-button-active': showMenu
            }
          )}>
          <span
            className={labelClassName}
            onMouseDown={this.handleMenuButtonClick}>
            {prefixTitle}
            {renderedLabel}
          </span>

          {icon && <span className="menu-section-icon">{icon}</span>}
          {disabled !== true &&
            !icon && ( // Only render Chevron if not disabled and icon is not present
              // Removed onClick from Chevron to prevent duplicate triggers of handleMenuButtonClick.
              // The button already handles onMouseDown, and click on Chevron was bubbling up and causing double execution.
              <Chevron className="menu-section-icon" />
            )}
          {secondaryLabel && (
            <span className="secondary-label">{secondaryLabel}</span>
          )}
        </button>
        <div
          className={classNames('chakra-menu-section', {
            'chakra-active-menu': this.state.showMenu
          })}
          data-testid="menu-button-menu"
          ref={this.menuRef}
          onKeyDown={this.handleKeyDown}
          onClick={this.handleMenuClick}>
          {this.props.children}
        </div>
      </div>
    );
  }
}

export { MenuButtonProps, MenuButton };
